import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaBookOpen,
  FaCamera,
  FaCheck,
  FaCopy,
  FaHome,
  FaImage,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import BackButton from "../ChildComponent/BackButton";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const TYPE_LABELS = {
  mcq: "MCQ",
  short: "Short Answer",
  long: "Long Answer",
  fill_blank: "Fill in the Blank",
  match: "Match the Following",
  true_false: "True / False",
};

const TYPES_WITH_OPTIONS = new Set(["mcq", "match", "true_false", "fill_blank"]);

//Created By Cursor
function newOption(text = "", isCorrect = false, pair = "", role = "") {
  return {
    id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    text,
    is_correct: !!isCorrect,
    pair: pair || "",
    role: role || (isCorrect ? "answer" : "option"),
    left_label: "",
    right_label: "",
  };
}

//Created By Cursor
function matchLetter(idx) {
  return String.fromCharCode(65 + idx);
}

//Created By Cursor
function defaultMatchOptions(count = 3) {
  const rows = [];
  for (let i = 0; i < count; i++) {
    rows.push({
      ...newOption("", false, "", "left"),
      left_label: String(i + 1),
    });
  }
  for (let i = 0; i < count; i++) {
    rows.push({
      ...newOption("", false, "", "right"),
      right_label: matchLetter(i),
    });
  }
  for (let i = 0; i < count; i++) {
    rows.push({
      ...newOption("", true, "", "key"),
      left_label: String(i + 1),
      right_label: "",
      is_correct: true,
    });
  }
  return rows;
}

//Created By Cursor
function defaultOptionsForType(type) {
  if (type === "true_false") {
    return [newOption("True", true), newOption("False", false)];
  }
  if (type === "match") {
    return defaultMatchOptions(3);
  }
  if (type === "fill_blank") {
    return [newOption("", true, "", "answer")];
  }
  if (type === "mcq") {
    return [
      newOption("", true),
      newOption(""),
      newOption(""),
      newOption(""),
    ];
  }
  return [];
}

//Created By Cursor
function parseMatchOptions(parsed) {
  // New shape: { column_a, column_b, correct }
  if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
    const leftSrc = parsed.column_a || parsed.left || [];
    const rightSrc = parsed.column_b || parsed.right || [];
    const correct = parsed.correct || parsed.answers || {};
    const n = Math.max(leftSrc.length, rightSrc.length, 2);
    const rows = [];
    for (let i = 0; i < n; i++) {
      const L = leftSrc[i];
      rows.push({
        ...newOption(
          typeof L === "string" ? L : L?.text || "",
          false,
          "",
          "left"
        ),
        left_label: String(
          (typeof L === "object" && L?.label) || i + 1
        ),
      });
    }
    for (let i = 0; i < n; i++) {
      const R = rightSrc[i];
      rows.push({
        ...newOption(
          typeof R === "string" ? R : R?.text || "",
          false,
          "",
          "right"
        ),
        right_label: String(
          (typeof R === "object" && R?.label) || matchLetter(i)
        ),
      });
    }
    for (let i = 0; i < n; i++) {
      const leftLabel = String(i + 1);
      const rightLabel =
        correct[leftLabel] ||
        correct[String(i)] ||
        (Array.isArray(correct) ? correct[i] : "") ||
        "";
      rows.push({
        ...newOption("", true, "", "key"),
        left_label: leftLabel,
        right_label: String(rightLabel || ""),
        is_correct: true,
      });
    }
    return rows;
  }

  // Legacy array: [{ text, pair }]
  if (Array.isArray(parsed) && parsed.length) {
    const lefts = [];
    const rights = [];
    const keys = [];
    parsed.forEach((item, idx) => {
      if (typeof item === "string") {
        lefts.push({
          ...newOption(item, false, "", "left"),
          left_label: String(idx + 1),
        });
        rights.push({
          ...newOption("", false, "", "right"),
          right_label: matchLetter(idx),
        });
        keys.push({
          ...newOption("", true, "", "key"),
          left_label: String(idx + 1),
          right_label: matchLetter(idx),
          is_correct: true,
        });
        return;
      }
      if (item.role === "left" || item.role === "right" || item.role === "key") {
        lefts.length = 0;
        return;
      }
      lefts.push({
        ...newOption(item.text || item.left || "", false, "", "left"),
        left_label: String(item.left_label || idx + 1),
      });
      rights.push({
        ...newOption(
          item.pair || item.right || item.correct_match || "",
          false,
          "",
          "right"
        ),
        right_label: String(item.right_label || matchLetter(idx)),
      });
      keys.push({
        ...newOption("", true, "", "key"),
        left_label: String(item.left_label || idx + 1),
        right_label: String(
          item.match_key || item.right_label || matchLetter(idx)
        ),
        is_correct: true,
      });
    });
    if (parsed.some((i) => i && (i.role === "left" || i.role === "right" || i.role === "key"))) {
      return parsed.map((item, idx) => ({
        ...newOption(
          item.text || "",
          !!item.is_correct,
          item.pair || "",
          item.role || "left"
        ),
        left_label: item.left_label || (item.role === "left" ? String(idx + 1) : ""),
        right_label:
          item.right_label ||
          (item.role === "right" ? matchLetter(idx) : item.match_key || ""),
      }));
    }
    return [...lefts, ...rights, ...keys];
  }
  return defaultMatchOptions(3);
}

//Created By Cursor
function parseOptionsJson(raw, type) {
  if (!raw) return defaultOptionsForType(type);
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return defaultOptionsForType(type);
    }
  }
  if (type === "match") {
    return parseMatchOptions(parsed);
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return defaultOptionsForType(type);
  }
  const mapped = parsed.map((item, idx) => {
    if (typeof item === "string") {
      return newOption(
        item,
        idx === 0,
        "",
        type === "fill_blank" && idx === 0 ? "answer" : "option"
      );
    }
    const pair =
      item.pair ||
      item.right ||
      item.match ||
      item.correct_match ||
      item.correctMatch ||
      "";
    const isCorrect =
      !!item.is_correct || !!item.correct || !!item.isCorrect;
    const role =
      item.role ||
      (type === "fill_blank" ? (isCorrect ? "answer" : "option") : "");
    return newOption(
      item.text || item.label || item.left || item.answer || "",
      isCorrect,
      pair,
      role
    );
  });

  if (type === "fill_blank") {
    const hasAnswer = mapped.some((o) => o.role === "answer" || o.is_correct);
    if (!hasAnswer) {
      return [newOption("", true, "", "answer"), ...mapped];
    }
    let answerSeen = false;
    return mapped.map((o) => {
      if ((o.role === "answer" || o.is_correct) && !answerSeen) {
        answerSeen = true;
        return { ...o, role: "answer", is_correct: true };
      }
      return { ...o, role: "option", is_correct: false };
    });
  }
  return mapped;
}

//Created By Cursor
function serializeOptions(options, type) {
  if (!TYPES_WITH_OPTIONS.has(type)) return "";
  if (type === "match") {
    const lefts = (options || []).filter((o) => o.role === "left");
    const rights = (options || []).filter((o) => o.role === "right");
    const keys = (options || []).filter((o) => o.role === "key");
    const column_a = lefts.map((o, i) => ({
      label: String(o.left_label || i + 1),
      text: String(o.text || "").trim(),
    }));
    const column_b = rights.map((o, i) => ({
      label: String(o.right_label || matchLetter(i)),
      text: String(o.text || "").trim(),
    }));
    const correct = {};
    keys.forEach((o, i) => {
      const leftLabel = String(o.left_label || i + 1);
      const rightLabel = String(o.right_label || "").trim();
      if (rightLabel) correct[leftLabel] = rightLabel;
    });
    const hasContent =
      column_a.some((x) => x.text) || column_b.some((x) => x.text);
    if (!hasContent) return "";
    return JSON.stringify({ column_a, column_b, correct });
  }
  const cleaned = (options || [])
    .map((o) => {
      const text = String(o.text || "").trim();
      if (type === "fill_blank") {
        const isAnswer = o.role === "answer" || !!o.is_correct;
        return {
          text,
          is_correct: isAnswer,
          role: isAnswer ? "answer" : "option",
        };
      }
      return { text, is_correct: !!o.is_correct };
    })
    .filter((o) => o.text.length > 0);
  return cleaned.length ? JSON.stringify(cleaned) : "";
}

//Created By Cursor
function optionsAreValid(type, options) {
  if (!TYPES_WITH_OPTIONS.has(type)) return true;
  const filled = (options || []).filter((o) => String(o.text || "").trim());
  if (type === "true_false") {
    return filled.length === 2 && filled.some((o) => o.is_correct);
  }
  if (type === "match") {
    const lefts = (options || []).filter((o) => o.role === "left");
    const rights = (options || []).filter((o) => o.role === "right");
    const keys = (options || []).filter((o) => o.role === "key");
    if (lefts.length < 2 || rights.length < 2) return false;
    if (lefts.some((o) => !String(o.text || "").trim())) return false;
    if (rights.some((o) => !String(o.text || "").trim())) return false;
    if (keys.length !== lefts.length) return false;
    if (keys.some((o) => !String(o.right_label || "").trim())) return false;
    const used = new Set(keys.map((o) => String(o.right_label)));
    return used.size === keys.length;
  }
  if (type === "fill_blank") {
    const answer = (options || []).find(
      (o) => o.role === "answer" || o.is_correct
    );
    return String(answer?.text || "").trim().length > 0;
  }
  return filled.length >= 2 && filled.some((o) => o.is_correct);
}

//Created By Cursor
function subjectsForClass(classId, rows = []) {
  const seen = new Map();
  rows
    .filter((r) => String(r.class_id) === String(classId))
    .forEach((r) => {
      if (!seen.has(String(r.subject_id))) seen.set(String(r.subject_id), r);
    });
  return Array.from(seen.values());
}

//Created By Cursor
function subjectLabel(row) {
  return row?.subject_name || row?.book_name || row?.subject_id || "Subject";
}

//Created By Cursor
function OptionsEditor({ type, options, onChange }) {
  if (!TYPES_WITH_OPTIONS.has(type)) return null;

  //Created By Cursor
  const updateAt = (id, patch) => {
    onChange(
      options.map((o) => {
        if (o.id !== id) {
          if (patch.is_correct && type !== "match" && type !== "fill_blank") {
            return { ...o, is_correct: false };
          }
          return o;
        }
        return { ...o, ...patch };
      })
    );
  };

  //Created By Cursor
  const removeAt = (id) => {
    const target = options.find((o) => o.id === id);
    if (type === "fill_blank" && (target?.role === "answer" || target?.is_correct)) {
      return;
    }
    if (type !== "fill_blank" && options.length <= 2) return;
    onChange(options.filter((o) => o.id !== id));
  };

  if (type === "match") {
    const lefts = options.filter((o) => o.role === "left");
    const rights = options.filter((o) => o.role === "right");
    const keys = options.filter((o) => o.role === "key");
    const count = Math.max(lefts.length, rights.length, 2);

    //Created By Cursor
    const rebuildMatch = (nextLefts, nextRights, nextKeys) => {
      const L = nextLefts.map((o, i) => ({
        ...o,
        role: "left",
        left_label: String(i + 1),
      }));
      const R = nextRights.map((o, i) => ({
        ...o,
        role: "right",
        right_label: matchLetter(i),
      }));
      const K = nextKeys.map((o, i) => ({
        ...o,
        role: "key",
        left_label: String(i + 1),
        is_correct: true,
      }));
      onChange([...L, ...R, ...K]);
    };

    //Created By Cursor
    const addMatchRow = () => {
      const i = lefts.length;
      rebuildMatch(
        [...lefts, { ...newOption("", false, "", "left"), left_label: String(i + 1) }],
        [
          ...rights,
          {
            ...newOption("", false, "", "right"),
            right_label: matchLetter(i),
          },
        ],
        [
          ...keys,
          {
            ...newOption("", true, "", "key"),
            left_label: String(i + 1),
            right_label: "",
            is_correct: true,
          },
        ]
      );
    };

    //Created By Cursor
    const removeMatchRow = (idx) => {
      if (lefts.length <= 2) return;
      rebuildMatch(
        lefts.filter((_, i) => i !== idx),
        rights.filter((_, i) => i !== idx),
        keys.filter((_, i) => i !== idx)
      );
    };

    return (
      <div className="qb-options qb-options--match">
        <div className="qb-options__head">
          <div>
            <strong>Match the following</strong>
            <p>
              Column 1 uses 1, 2, 3… and Column 2 uses A, B, C… Set correct
              matching below so the question columns stay unchanged.
            </p>
          </div>
          <button
            type="button"
            className="qb-btn qb-btn--ghost qb-btn--sm"
            onClick={addMatchRow}
          >
            <FaPlus /> Add row
          </button>
        </div>

        <div className="qb-match-cols">
          <div className="qb-match-col">
            <div className="qb-match-col__title">Column 1</div>
            {Array.from({ length: count }).map((_, idx) => {
              const opt = lefts[idx] || {
                ...newOption("", false, "", "left"),
                left_label: String(idx + 1),
              };
              return (
                <div key={opt.id || `left-${idx}`} className="qb-options__row">
                  <span className="qb-options__letter">{idx + 1}</span>
                  <input
                    value={opt.text || ""}
                    onChange={(e) => {
                      const next = [...lefts];
                      while (next.length <= idx) {
                        next.push({
                          ...newOption("", false, "", "left"),
                          left_label: String(next.length + 1),
                        });
                      }
                      next[idx] = { ...next[idx], text: e.target.value };
                      rebuildMatch(next, rights, keys);
                    }}
                    placeholder={`Item ${idx + 1}`}
                  />
                  {count > 2 ? (
                    <button
                      type="button"
                      className="qb-icon-btn qb-icon-btn--danger"
                      onClick={() => removeMatchRow(idx)}
                      aria-label="Remove row"
                    >
                      <FaTrash />
                    </button>
                  ) : (
                    <span className="qb-match-col__spacer" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="qb-match-col">
            <div className="qb-match-col__title">Column 2</div>
            {Array.from({ length: count }).map((_, idx) => {
              const opt = rights[idx] || {
                ...newOption("", false, "", "right"),
                right_label: matchLetter(idx),
              };
              return (
                <div key={opt.id || `right-${idx}`} className="qb-options__row">
                  <span className="qb-options__letter qb-options__letter--alpha">
                    {matchLetter(idx)}
                  </span>
                  <input
                    value={opt.text || ""}
                    onChange={(e) => {
                      const next = [...rights];
                      while (next.length <= idx) {
                        next.push({
                          ...newOption("", false, "", "right"),
                          right_label: matchLetter(next.length),
                        });
                      }
                      next[idx] = { ...next[idx], text: e.target.value };
                      rebuildMatch(lefts, next, keys);
                    }}
                    placeholder={`Option ${matchLetter(idx)}`}
                  />
                  <span className="qb-match-col__spacer" />
                </div>
              );
            })}
          </div>
        </div>

        <div className="qb-match-keys">
          <div className="qb-match-keys__head">
            <strong>Correct matching</strong>
            <p>Answer key only — does not change Column 1 / Column 2 text.</p>
          </div>
          <div className="qb-match-keys__list">
            {Array.from({ length: count }).map((_, idx) => {
              const key =
                keys[idx] || {
                  ...newOption("", true, "", "key"),
                  left_label: String(idx + 1),
                  right_label: "",
                };
              return (
                <div key={key.id || `key-${idx}`} className="qb-match-keys__row">
                  <span className="qb-options__letter">{idx + 1}</span>
                  <span className="qb-options__pair-sep">→</span>
                  <select
                    value={key.right_label || ""}
                    onChange={(e) => {
                      const next = [...keys];
                      while (next.length <= idx) {
                        next.push({
                          ...newOption("", true, "", "key"),
                          left_label: String(next.length + 1),
                          right_label: "",
                          is_correct: true,
                        });
                      }
                      next[idx] = {
                        ...next[idx],
                        left_label: String(idx + 1),
                        right_label: e.target.value,
                        is_correct: true,
                      };
                      rebuildMatch(lefts, rights, next);
                    }}
                  >
                    <option value="">Select</option>
                    {Array.from({ length: count }).map((__, rIdx) => (
                      <option key={matchLetter(rIdx)} value={matchLetter(rIdx)}>
                        {matchLetter(rIdx)}
                        {rights[rIdx]?.text
                          ? ` — ${rights[rIdx].text}`
                          : ""}
                      </option>
                    ))}
                  </select>
                  <span className="qb-options__match-tag">Correct</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (type === "fill_blank") {
    let answer =
      options.find((o) => o.role === "answer" || o.is_correct) || null;
    const distractors = options.filter(
      (o) => !(o.role === "answer" || o.is_correct)
    );
    if (!answer) {
      answer = newOption("", true, "", "answer");
    }

    //Created By Cursor
    const setAnswerText = (text) => {
      const rest = options.filter(
        (o) => !(o.role === "answer" || o.is_correct)
      );
      onChange([
        { ...answer, text, is_correct: true, role: "answer" },
        ...rest,
      ]);
    };

    //Created By Cursor
    const addDistractor = () => {
      const base = options.some((o) => o.role === "answer" || o.is_correct)
        ? options
        : [{ ...answer, is_correct: true, role: "answer" }, ...options];
      onChange([...base, newOption("", false, "", "option")]);
    };

    return (
      <div className="qb-options qb-options--fill_blank">
        <div className="qb-fill-answer">
          <label className="qb-field qb-field--answer">
            <span>Answer</span>
            <input
              value={answer.text || ""}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="Correct answer for the blank"
            />
          </label>
          <p className="qb-fill-answer__hint">
            Required. Optional word-bank options can be added below.
          </p>
        </div>

        <div className="qb-options__head">
          <div>
            <strong>Options (optional)</strong>
            <p>Word bank / distractors — leave empty if not needed.</p>
          </div>
          <button
            type="button"
            className="qb-btn qb-btn--ghost qb-btn--sm"
            onClick={addDistractor}
          >
            <FaPlus /> Add option
          </button>
        </div>

        {distractors.length === 0 ? (
          <p className="qb-options__empty">No optional options added.</p>
        ) : (
          <div className="qb-options__list">
            {distractors.map((opt, idx) => (
              <div key={opt.id} className="qb-options__row">
                <span className="qb-options__letter">
                  {String.fromCharCode(65 + idx)}
                </span>
                <input
                  value={opt.text}
                  onChange={(e) => updateAt(opt.id, { text: e.target.value })}
                  placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                />
                <button
                  type="button"
                  className="qb-icon-btn qb-icon-btn--danger"
                  onClick={() => removeAt(opt.id)}
                  aria-label="Remove option"
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const title =
    type === "mcq" ? "Answer options" : "True / False";

  const hint = "Mark the correct answer. At least two options required.";

  const canAdd = type === "mcq";

  return (
    <div className={`qb-options qb-options--${type}`}>
      <div className="qb-options__head">
        <div>
          <strong>{title}</strong>
          <p>{hint}</p>
        </div>
        {canAdd ? (
          <button
            type="button"
            className="qb-btn qb-btn--ghost qb-btn--sm"
            onClick={() => onChange([...options, newOption("", false)])}
          >
            <FaPlus /> Add option
          </button>
        ) : null}
      </div>

      <div className="qb-options__list">
        {options.map((opt, idx) => (
          <div key={opt.id} className="qb-options__row">
            <span className="qb-options__letter">
              {String.fromCharCode(65 + idx)}
            </span>
            <input
              value={opt.text}
              onChange={(e) => updateAt(opt.id, { text: e.target.value })}
              placeholder={`Option ${String.fromCharCode(65 + idx)}`}
              disabled={type === "true_false"}
            />
            <label className="qb-options__correct">
              <input
                type="radio"
                name={`qb-correct-${type}`}
                checked={!!opt.is_correct}
                onChange={() => updateAt(opt.id, { is_correct: true })}
              />
              Correct
            </label>
            {type !== "true_false" ? (
              <button
                type="button"
                className="qb-icon-btn qb-icon-btn--danger"
                disabled={options.length <= 2}
                onClick={() => removeAt(opt.id)}
                aria-label="Remove option"
              >
                <FaTrash />
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

//Created By Cursor
export default function AddQuestionBank() {
  const history = useHistory();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [classes, setClasses] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [sourceType, setSourceType] = useState("manual");
  const [form, setForm] = useState({
    question_text: "",
    type: "mcq",
    class_id: "",
    subject_id: "",
    chapter: "",
    difficulty: "Easy",
    marks: 1,
    source_scan_id: "",
    options: defaultOptionsForType("mcq"),
  });
  const [scanPreview, setScanPreview] = useState(null);
  const [scanFile, setScanFile] = useState(null);
  const [scanStatus, setScanStatus] = useState("idle");
  const [scanBusy, setScanBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [chapterSuggestions, setChapterSuggestions] = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySelected, setCopySelected] = useState([]);
  const [copying, setCopying] = useState(false);
  const scanInputRef = useRef(null);

  const subjectOptions = useMemo(() => {
    const rows = subjectsForClass(form.class_id, classSubjects);
    const sid = String(form.subject_id || "");
    if (
      sid &&
      !rows.some((s) => String(s.subject_id) === sid)
    ) {
      return [
        {
          subject_id: sid,
          subject_name: `Current (${sid})`,
          book_name: `Current (${sid})`,
        },
        ...rows,
      ];
    }
    return rows;
  }, [form.class_id, form.subject_id, classSubjects]);

  //Created By Cursor — unique chapter/topic values from previous bank entries
  const buildChapterSuggestions = useCallback((rows = []) => {
    const seen = new Set();
    const list = [];
    rows.forEach((q) => {
      const ch = String(q.chapter || "").trim();
      if (!ch) return;
      const key = ch.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      list.push(ch);
    });
    list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return list;
  }, []);

  //Created By Cursor
  const loadRecent = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;
    setRecentLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getQuestionBank.php`, {
        params: {
          school_code: schoolCode,
          session_id: sessionId || undefined,
        },
      });
      const rows =
        res.data?.api_status === "OK" && Array.isArray(res.data.data)
          ? res.data.data
          : [];
      setRecent(rows.slice(0, 8));
      setChapterSuggestions((prev) => {
        const merged = buildChapterSuggestions([...rows, ...prev.map((c) => ({ chapter: c }))]);
        return merged;
      });
    } catch (err) {
      console.error("[AddQuestionBank] recent error:", err);
      setRecent([]);
    } finally {
      setRecentLoading(false);
    }
  }, [sessionId, buildChapterSuggestions]);

  //Created By Cursor — refresh topic suggestions for selected class/subject
  const loadChapterSuggestions = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;
    try {
      const res = await axios.get(`${API_BASE()}getQuestionBank.php`, {
        params: {
          school_code: schoolCode,
          session_id: sessionId || undefined,
          class_id: form.class_id || undefined,
          subject_id: form.subject_id || undefined,
        },
      });
      const rows =
        res.data?.api_status === "OK" && Array.isArray(res.data.data)
          ? res.data.data
          : [];
      setChapterSuggestions(buildChapterSuggestions(rows));
    } catch (err) {
      console.error("[AddQuestionBank] chapter suggest error:", err);
    }
  }, [sessionId, form.class_id, form.subject_id, buildChapterSuggestions]);

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
    const current = SessionManager.getCurrentSession()?.[0];
    setSessionId(current?.session_id || "");
  }, []);

  //Created By Cursor
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        return;
      }
      try {
        const [classRes, subjectRes] = await Promise.all([
          axios.get(`${API_BASE()}getQuestionBankClasses.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId || undefined,
            },
          }),
          axios.get(`${API_BASE()}getQuestionBankClassSubjects.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId || undefined,
            },
          }),
        ]);
        if (cancelled) return;
        const cls =
          classRes.data?.api_status === "OK" && Array.isArray(classRes.data.data)
            ? classRes.data.data
            : [];
        const subs =
          subjectRes.data?.api_status === "OK" &&
          Array.isArray(subjectRes.data.data)
            ? subjectRes.data.data
            : [];
        setClasses(cls);
        setClassSubjects(subs);

        if (isEdit) {
          const qRes = await axios.get(`${API_BASE()}getQuestionBankById.php`, {
            params: { school_code: schoolCode, id: editId },
          });
          if (cancelled) return;
          if (qRes.data?.api_status === "OK" && qRes.data.data) {
            const q = qRes.data.data;
            setForm({
              question_text: q.question_text || "",
              type: q.type || "mcq",
              class_id: q.class_id != null ? String(q.class_id) : "",
              subject_id: q.subject_id != null ? String(q.subject_id) : "",
              chapter: q.chapter || "",
              difficulty: q.difficulty || "Easy",
              marks: Number(q.marks || 1),
              source_scan_id: q.source_scan_id || "",
              options: parseOptionsJson(q.options_json, q.type || "mcq"),
            });
            setSourceType(q.source_type || "manual");
          } else {
            myToast.notify_warning("Question not found.");
            history.replace("/AdminMain/QuestionBank");
          }
        } else if (cls[0]) {
          const nextSubs = subjectsForClass(cls[0].class_id, subs);
          setForm((f) => ({
            ...f,
            class_id: f.class_id || cls[0].class_id,
            subject_id: f.subject_id || nextSubs[0]?.subject_id || "",
          }));
        }
      } catch (err) {
        console.error("[AddQuestionBank] load error:", err);
        myToast.notify_warning("Could not load form data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, isEdit, editId, history]);

  //Created By Cursor
  useEffect(() => {
    if (!loading) loadRecent();
  }, [loading, loadRecent]);

  //Created By Cursor
  useEffect(() => {
    if (!loading && form.class_id) loadChapterSuggestions();
  }, [loading, form.class_id, form.subject_id, loadChapterSuggestions]);

  //Created By Cursor
  const filteredChapterSuggestions = useMemo(() => {
    const q = String(form.chapter || "").trim().toLowerCase();
    if (!q) return chapterSuggestions;
    return chapterSuggestions.filter((c) => c.toLowerCase().includes(q));
  }, [chapterSuggestions, form.chapter]);

  //Created By Cursor — other classes that share this question's subject
  const copyTargetClasses = useMemo(() => {
    const sid = String(form.subject_id || "");
    const current = String(form.class_id || "");
    if (!sid) return [];
    const withSubject = new Set(
      classSubjects
        .filter((r) => String(r.subject_id) === sid)
        .map((r) => String(r.class_id))
    );
    return classes.filter(
      (c) =>
        String(c.class_id) !== current && withSubject.has(String(c.class_id))
    );
  }, [classes, classSubjects, form.class_id, form.subject_id]);

  //Created By Cursor
  const currentSubjectLabel = useMemo(() => {
    const sid = String(form.subject_id || "");
    if (!sid) return "";
    const row =
      classSubjects.find((r) => String(r.subject_id) === sid) ||
      subjectOptions.find((r) => String(r.subject_id) === sid);
    return subjectLabel(row) || sid;
  }, [classSubjects, subjectOptions, form.subject_id]);

  //Created By Cursor
  const handleClassChange = (classIdStr) => {
    const nextSubjects = subjectsForClass(classIdStr, classSubjects);
    setForm((f) => {
      const keep = nextSubjects.some(
        (s) => String(s.subject_id) === String(f.subject_id)
      );
      return {
        ...f,
        class_id: String(classIdStr || ""),
        subject_id: keep
          ? String(f.subject_id)
          : nextSubjects[0]?.subject_id != null
            ? String(nextSubjects[0].subject_id)
            : "",
      };
    });
  };

  //Created By Cursor
  const handleTypeChange = (nextType) => {
    setForm((f) => ({
      ...f,
      type: nextType,
      options: TYPES_WITH_OPTIONS.has(nextType)
        ? f.type === nextType && f.options?.length
          ? f.options
          : defaultOptionsForType(nextType)
        : [],
    }));
  };

  //Created By Cursor
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanFile(file);
    setScanPreview(URL.createObjectURL(file));
    setScanStatus("uploaded");
    setForm((f) => ({ ...f, source_scan_id: "" }));
  };

  //Created By Cursor
  const runExtraction = async () => {
    if (!scanFile) return;
    const schoolCode = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails() || {};
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    setScanBusy(true);
    setScanStatus("extracting");
    try {
      const fd = new FormData();
      fd.append("school_code", schoolCode);
      fd.append("uploaded_by", staff.staff_id || "");
      fd.append("image", scanFile);
      // Do not set Content-Type manually — browser must add multipart boundary
      const uploadRes = await axios.post(
        `${API_BASE()}uploadQuestionScan.php`,
        fd
      );
      if (uploadRes.data?.api_status !== "OK" || !uploadRes.data?.data?.id) {
        throw new Error(
          uploadRes.data?.api_remark ||
            uploadRes.data?.message ||
            "Upload failed"
        );
      }
      const scanId = uploadRes.data.data.id;
      const extractRes = await axios.post(
        `${API_BASE()}extractQuestionScan.php`,
        { school_code: schoolCode, id: scanId }
      );
      if (extractRes.data?.api_status !== "OK") {
        throw new Error(
          extractRes.data?.api_remark ||
            extractRes.data?.message ||
            "Extract failed"
        );
      }
      const extracted = extractRes.data?.data?.extracted || {};
      const questionText = String(extracted.question_text || "").trim();
      if (!questionText) {
        throw new Error(
          extracted.note ||
            "No question text found in the image. Try a clearer photo."
        );
      }
      const nextType = extracted.type || "short";
      setForm((f) => ({
        ...f,
        question_text: questionText,
        type: nextType,
        marks: Number(extracted.suggested_marks || f.marks || 1),
        source_scan_id: scanId,
        options: TYPES_WITH_OPTIONS.has(nextType)
          ? parseOptionsJson(extracted.options || [], nextType)
          : [],
      }));
      setSourceType("scanned");
      setScanStatus("extracted");
      if (extracted.note) myToast.notify_warning(extracted.note);
      else myToast.notify_success("Question text extracted — review below.");
    } catch (err) {
      console.error("[AddQuestionBank] scan error:", err);
      setScanStatus("uploaded");
      const apiMsg =
        err?.response?.data?.api_remark ||
        err?.response?.data?.message ||
        err?.message;
      myToast.notify_warning(
        apiMsg || "Could not extract text. Enter question manually."
      );
    } finally {
      setScanBusy(false);
    }
  };

  const formValid =
    String(form.question_text || "").trim().length > 0 &&
    String(form.chapter || "").trim().length > 0 &&
    form.class_id &&
    form.subject_id &&
    optionsAreValid(form.type, form.options);

  const canSave = formValid && !saving && !scanBusy;

  const saveLabel = saving
    ? "Saving…"
    : isEdit
      ? "Update question"
      : "Save to bank";

  const saveLabelShort = saving
    ? "Saving…"
    : isEdit
      ? "Update"
      : "Save to bank";

  //Created By Cursor
  const goBack = useCallback(() => {
    history.push("/AdminMain/QuestionBank");
  }, [history]);

  //Created By Cursor
  const openCopyDialog = useCallback(() => {
    setCopySelected([]);
    setCopyOpen(true);
  }, []);

  //Created By Cursor
  const closeCopyDialog = useCallback(() => {
    if (copying) return;
    setCopyOpen(false);
    setCopySelected([]);
  }, [copying]);

  //Created By Cursor
  const toggleCopyClass = useCallback((classId) => {
    const id = String(classId);
    setCopySelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  //Created By Cursor
  const handleCopyToClasses = useCallback(async () => {
    if (!isEdit || !editId || copySelected.length === 0 || copying) return;
    const schoolCode = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails() || {};
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    setCopying(true);
    try {
      const body = new URLSearchParams();
      body.set("school_code", schoolCode);
      body.set("id", String(editId));
      body.set("target_class_ids", JSON.stringify(copySelected));
      body.set("added_by", staff.staff_id || "");
      const res = await axios.post(
        `${API_BASE()}copyQuestionToClasses.php`,
        body,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      if (res.data?.api_status === "OK") {
        const n = Number(res.data?.data?.copied_count || copySelected.length);
        myToast.notify_success(
          n === 1
            ? "Question copied to 1 class."
            : `Question copied to ${n} classes.`
        );
        setCopyOpen(false);
        setCopySelected([]);
      } else {
        myToast.notify_warning(
          res.data?.api_remark || res.data?.message || "Copy failed."
        );
      }
    } catch (err) {
      console.error("[AddQuestionBank] copy error:", err);
      myToast.notify_warning(
        err?.response?.data?.api_remark ||
          err?.response?.data?.message ||
          "Failed to copy question."
      );
    } finally {
      setCopying(false);
    }
  }, [isEdit, editId, copySelected, copying]);

  //Created By Cursor
  const handleSave = async () => {
    if (!canSave) return;
    const schoolCode = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails() || {};
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    const classId = String(form.class_id || "").trim();
    const subjectId = String(form.subject_id || "").trim();
    const chapter = String(form.chapter || "").trim();
    const questionText = String(form.question_text || "").trim();
    if (!classId || !subjectId || !chapter || !questionText) {
      myToast.notify_warning("Class, subject, chapter/topic, and question text are required.");
      return;
    }

    setSaving(true);
    try {
      const optionsJson = serializeOptions(form.options, form.type);
      const body = new URLSearchParams();
      body.set("school_code", schoolCode);
      body.set("session_id", sessionId || "");
      body.set("class_id", classId);
      body.set("subject_id", subjectId);
      body.set("question_text", questionText);
      body.set("type", form.type || "short");
      body.set("chapter", chapter);
      body.set("difficulty", form.difficulty || "Easy");
      body.set("marks", String(Number(form.marks) || 1));
      body.set("options_json", optionsJson || "");
      body.set(
        "source_type",
        isEdit ? sourceType || "manual" : form.source_scan_id ? "scanned" : "manual"
      );
      body.set("source_scan_id", form.source_scan_id ? String(form.source_scan_id) : "");
      body.set("added_by", staff.staff_id || "");

      let res;
      if (isEdit) {
        body.set("id", String(editId));
        res = await axios.post(`${API_BASE()}updateQuestionBank.php`, body, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
      } else if (form.source_scan_id) {
        body.set("id", String(form.source_scan_id));
        body.set("scan_id", String(form.source_scan_id));
        res = await axios.post(`${API_BASE()}confirmQuestionScan.php`, body, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
      } else {
        res = await axios.post(`${API_BASE()}addQuestionBank.php`, body, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
      }

      if (res.data?.api_status === "OK") {
        setSaving(false);
        myToast.notify_success(isEdit ? "Question updated." : "Question saved.");
        if (isEdit) {
          goBack();
          return;
        }
        const keepClass = classId;
        const keepSubject = subjectId;
        setForm({
          question_text: "",
          type: "mcq",
          class_id: keepClass,
          subject_id: keepSubject,
          chapter: "",
          difficulty: "Easy",
          marks: 1,
          source_scan_id: "",
          options: defaultOptionsForType("mcq"),
        });
        setSourceType("manual");
        setScanPreview(null);
        setScanFile(null);
        setScanStatus("idle");
        if (scanInputRef.current) scanInputRef.current.value = "";
        setChapterSuggestions((prev) => {
          const next = String(chapter || "").trim();
          if (!next) return prev;
          if (prev.some((c) => c.toLowerCase() === next.toLowerCase())) {
            return prev;
          }
          return [...prev, next].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          );
        });
        loadRecent();
        loadChapterSuggestions();
      } else {
        myToast.notify_warning(
          res.data?.api_remark || res.data?.message || "Save failed."
        );
      }
    } catch (err) {
      console.error("[AddQuestionBank] save error:", err);
      myToast.notify_warning(
        err?.response?.data?.api_remark ||
          err?.response?.data?.message ||
          "Failed to save question."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page qb-page qb-add-page"
      style={{ background: "#f5f7fb" }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="qb-crumb-link"
                onClick={goBack}
              >
                Question Bank
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>
                {isEdit ? "Edit Question" : "Add Question"}
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBookOpen />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">
                {isEdit ? "Edit question" : "Add question"}
              </h1>
              <p className="sm-hero__sub">
                {isEdit
                  ? "Update text, tags, options, or marks for this bank entry."
                  : "Enter the question below, or scan a photo from a book to fill it in."}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {!isEdit ? (
              <>
                <input
                  ref={scanInputRef}
                  type="file"
                  accept="image/*"
                  className="qb-scan-input-hidden"
                  onChange={handleFile}
                />
                <button
                  type="button"
                  className={`qb-scan-hero-btn${
                    scanPreview ? " is-active" : ""
                  }`}
                  onClick={() => scanInputRef.current?.click()}
                  disabled={scanBusy}
                >
                  <span className="qb-scan-hero-btn__icon" aria-hidden>
                    <FaCamera />
                  </span>
                  <span className="qb-scan-hero-btn__copy">
                    <strong>Scan from book</strong>
                    <small>Upload photo → extract → review</small>
                  </span>
                </button>
              </>
            ) : (
              <button
                type="button"
                className="qb-hero-btn qb-hero-btn--ghost"
                onClick={openCopyDialog}
                disabled={loading || !form.subject_id}
                title="Copy this question to other classes with the same subject"
              >
                <FaCopy /> Copy To Other Class
              </button>
            )}
            <button
              type="button"
              className="qb-hero-btn qb-hero-btn--ghost"
              onClick={goBack}
            >
              <FaTimes /> Cancel
            </button>
            <button
              type="button"
              className="qb-hero-btn"
              disabled={!canSave}
              onClick={handleSave}
            >
              <FaSave /> {saveLabelShort}
            </button>
          </div>
        </header>

        <div className="qb-content qb-add-content">
          {loading ? (
            <MyLoader />
          ) : (
            <>
              {!isEdit && scanPreview ? (
                <div className="qb-scan qb-scan--page qb-scan--header-flow">
                  <div className="qb-scan__preview">
                    <img src={scanPreview} alt="Scanned question" />
                    <div className="qb-scan__preview-actions">
                      {scanStatus === "uploaded" ? (
                        <button
                          type="button"
                          className="qb-btn qb-btn--teal"
                          onClick={runExtraction}
                          disabled={scanBusy}
                        >
                          Extract question text
                        </button>
                      ) : null}
                      {scanStatus === "extracting" ? (
                        <p className="qb-hint">Reading the question…</p>
                      ) : null}
                      {scanStatus === "extracted" ? (
                        <p className="qb-extracted">
                          <FaImage /> Extracted — review and edit below before
                          saving
                        </p>
                      ) : null}
                      <button
                        type="button"
                        className="qb-btn qb-btn--ghost"
                        onClick={() => scanInputRef.current?.click()}
                      >
                        Replace image
                      </button>
                      <button
                        type="button"
                        className="qb-btn qb-btn--ghost"
                        onClick={() => {
                          setScanPreview(null);
                          setScanFile(null);
                          setScanStatus("idle");
                          setForm((f) => ({ ...f, source_scan_id: "" }));
                          if (scanInputRef.current) scanInputRef.current.value = "";
                        }}
                      >
                        Clear scan
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="qb-form qb-form--page">
                  <label className="qb-form__full">
                    <span>Question text</span>
                    <textarea
                      rows={3}
                      value={form.question_text}
                      onChange={(e) =>
                        setForm({ ...form, question_text: e.target.value })
                      }
                      placeholder="Enter the question..."
                    />
                  </label>

                  <div className="qb-meta-panel">
                    <div className="qb-meta-panel__row">
                      <label className="qb-field">
                        <span>Type</span>
                        <select
                          value={form.type}
                          onChange={(e) => handleTypeChange(e.target.value)}
                        >
                          {Object.entries(TYPE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="qb-field">
                        <span>Difficulty</span>
                        <select
                          value={form.difficulty}
                          onChange={(e) =>
                            setForm({ ...form, difficulty: e.target.value })
                          }
                        >
                          {["Easy", "Medium", "Hard"].map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="qb-field qb-field--marks">
                        <span>Marks</span>
                        <input
                          type="number"
                          min={1}
                          value={form.marks}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              marks: Number(e.target.value),
                            })
                          }
                        />
                      </label>
                      <label className="qb-field">
                        <span>Class</span>
                        <select
                          value={String(form.class_id ?? "")}
                          onChange={(e) => handleClassChange(e.target.value)}
                        >
                          {classes.map((c) => (
                            <option
                              key={c.class_id}
                              value={String(c.class_id)}
                            >
                              {c.class_name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="qb-field">
                        <span>Subject</span>
                        <select
                          value={String(form.subject_id ?? "")}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              subject_id: String(e.target.value),
                            })
                          }
                          disabled={!form.class_id}
                        >
                          <option value="">Select subject</option>
                          {subjectOptions.map((s) => (
                            <option
                              key={s.subject_id}
                              value={String(s.subject_id)}
                            >
                              {subjectLabel(s)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="qb-field qb-field--chapter">
                        <span>Chapter / topic</span>
                        <input
                          list="qb-chapter-suggest"
                          value={form.chapter}
                          onChange={(e) =>
                            setForm({ ...form, chapter: e.target.value })
                          }
                          placeholder="Type or pick a previous topic"
                          autoComplete="off"
                        />
                        <datalist id="qb-chapter-suggest">
                          {filteredChapterSuggestions.map((ch) => (
                            <option key={ch} value={ch} />
                          ))}
                        </datalist>
                      </label>
                    </div>
                  </div>

                  <OptionsEditor
                    type={form.type}
                    options={form.options || []}
                    onChange={(options) => setForm({ ...form, options })}
                  />
                </div>

              <div className="qb-add-actions">
                <button
                  type="button"
                  className="qb-btn qb-btn--ghost"
                  onClick={goBack}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="qb-btn qb-btn--primary"
                  disabled={!canSave}
                  onClick={handleSave}
                >
                  {saveLabel}
                </button>
              </div>

              {!isEdit ? (
                <section className="qb-recent">
                  <div className="qb-recent__head">
                    <div>
                      <h3>Recent questions</h3>
                      <p>Latest entries in the bank for this session.</p>
                    </div>
                    <button
                      type="button"
                      className="qb-btn qb-btn--ghost qb-btn--sm"
                      onClick={loadRecent}
                      disabled={recentLoading}
                    >
                      Refresh
                    </button>
                  </div>
                  {recentLoading && !recent.length ? (
                    <p className="qb-recent__empty">Loading…</p>
                  ) : recent.length === 0 ? (
                    <p className="qb-recent__empty">
                      No questions yet — saved items will appear here.
                    </p>
                  ) : (
                    <div className="qb-recent__list">
                      {recent.map((q) => (
                        <button
                          key={q.id}
                          type="button"
                          className="qb-recent__item"
                          onClick={() =>
                            history.push(`/AdminMain/AddQuestionBank/${q.id}`)
                          }
                        >
                          <span className="qb-recent__text">
                            {q.question_text}
                          </span>
                          <span className="qb-recent__meta">
                            <span>{TYPE_LABELS[q.type] || q.type}</span>
                            <span>{q.difficulty}</span>
                            <span>
                              {q.marks} {Number(q.marks) === 1 ? "mark" : "marks"}
                            </span>
                            {q.chapter ? <span>{q.chapter}</span> : null}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>

      {copyOpen ? (
        <div
          className="qb-modal-backdrop"
          role="presentation"
          onClick={closeCopyDialog}
        >
          <div
            className="qb-modal qb-modal--copy-class"
            role="dialog"
            aria-modal="true"
            aria-labelledby="qb-copy-class-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="qb-modal__head">
              <div className="qb-modal__head-copy">
                <p className="qb-modal__eyebrow">Question bank</p>
                <h2 id="qb-copy-class-title">Copy To Other Class</h2>
                <p className="qb-modal__sub">
                  Classes that share subject{" "}
                  <strong>{currentSubjectLabel || "—"}</strong>. Select one or
                  more targets.
                </p>
              </div>
              <button
                type="button"
                className="qb-icon-btn"
                aria-label="Close"
                disabled={copying}
                onClick={closeCopyDialog}
              >
                <FaTimes />
              </button>
            </div>

            <div className="qb-modal__body">
              <div className="qb-copy-head">
                <p className="qb-copy-head__label">Select class</p>
                <span className="qb-copy-head__count">
                  {copyTargetClasses.length} available
                </span>
              </div>

              {copying ? (
                <div className="qb-copy-loading">
                  <MyLoader />
                  <p>Copying question…</p>
                </div>
              ) : copyTargetClasses.length === 0 ? (
                <div className="qb-empty qb-empty--dashed">
                  <p className="qb-empty__title">No other classes available</p>
                  <p className="qb-empty__hint">
                    No other class has subject “{currentSubjectLabel || "this"}”
                    assigned.
                  </p>
                </div>
              ) : (
                <div className="qb-copy-grid">
                  {copyTargetClasses.map((c) => {
                    const id = String(c.class_id);
                    const selected = copySelected.includes(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`qb-copy-tile${
                          selected ? " is-selected" : ""
                        }`}
                        onClick={() => toggleCopyClass(id)}
                        aria-pressed={selected}
                      >
                        {selected ? (
                          <span className="qb-copy-tile__check" aria-hidden>
                            <FaCheck />
                          </span>
                        ) : null}
                        <span className="qb-copy-tile__name">
                          {c.class_name || id}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="qb-modal__foot">
              {copySelected.length > 0 ? (
                <span className="qb-copy-selected">
                  {copySelected.length} selected
                </span>
              ) : (
                <span className="qb-copy-selected qb-copy-selected--muted">
                  Select at least one class
                </span>
              )}
              <button
                type="button"
                className="qb-btn qb-btn--ghost"
                disabled={copying}
                onClick={closeCopyDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className="qb-btn qb-btn--teal"
                disabled={copying || copySelected.length === 0}
                onClick={handleCopyToClasses}
              >
                <FaCopy /> Copy
                {copySelected.length > 0 ? ` (${copySelected.length})` : ""}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
